import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getOrganizationClientRecordInvoiceCacheKey,
    getOrganizationClientRecordInvoiceInvoiceItemsCacheKey,
    getOrganizationClientRecordInvoicesCacheKey,
    onClientRecordInvoicesUpdate,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('UseOrganizationClientRecordInvoices', {
    failedToLoadInvoices: 'Failed to load invoices',
    failedToSendInvoice: 'Failed to send invoice',
    successfullySentInvoice: 'Successfully sent invoice',
    failedToLoadInvoice: 'Failed to load invoice',
    failedToLoadInvoiceItems: 'Failed to load invoice items',
    failedToLoadInvoiceInvoiceItems: 'Failed to load invoice line items',
    failedToVoidInvoice: 'Failed to void invoice',
    successfullyVoidedInvoice: 'Successfully voided invoice',
    failedToMarkPaidInvoice: 'Failed to mark invoice as paid',
    successfullyMarkedPaidInvoice: 'Successfully marked invoice as paid',
    failedToResendInvoice: 'Failed to resend invoice',
    successfullyResentInvoice: 'Successfully resent invoice',
    successfullyRefundedInvoice: 'Successfully refunded invoice',
    failedToRefundInvoice: 'Failed to refund invoice',
});

export type UseOrganizationClientRecordInvoicesArgs = {
    clientRecordId: string;
    invoiceId?: string;
    onInvoiceSent?: () => void;
    onInvoiceVoided?: () => void;
    onInvoiceMarkedPaid?: () => void;
    onInvoiceResent?: () => void;
    onInvoiceRefunded?: () => void;
};

export type UseOrganizationClientRecordInvoicesReturnValue = {
    sendInvoice: (invoice: APITypes.organizationService.CreateOrganizationClientRecordInvoiceRequest) => void;
    isSendingInvoice: boolean;
    voidInvoice: (invoiceId: string) => void;
    isVoidingInvoice: boolean;
    markPaidInvoice: (invoiceId: string) => void;
    isMarkingPaidInvoice: boolean;
    resendInvoice: (invoiceId: string) => void;
    isResendingInvoice: boolean;
    invoices: APITypes.organizationService.ListOrganizationClientRecordInvoicesResponse | null;
    isLoadingInvoices: boolean;
    isLoadingInvoice: boolean;
    invoice: APITypes.organizationService.GetOrganizationClientRecordInvoiceResponse | null;
    isLoadingInvoiceItems: boolean;
    invoiceItems: APITypes.organizationService.ListOrganizationClientRecordInvoiceItemsResponse | null;
    refundInvoice: (invoiceId: string) => void;
    isRefundingInvoice: boolean;
};

export default function useOrganizationClientRecordInvoices({
    clientRecordId,
    invoiceId,
    onInvoiceSent,
    onInvoiceVoided,
    onInvoiceMarkedPaid,
    onInvoiceResent,
    onInvoiceRefunded,
}: UseOrganizationClientRecordInvoicesArgs): UseOrganizationClientRecordInvoicesReturnValue {
    const organizationId = useOrganizationId();
    const notify = useNotify();
    const invoicesQuery = useQuery({
        queryKey: getOrganizationClientRecordInvoicesCacheKey(clientRecordId),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListOrganizationClientRecordInvoicesResponse>(
                urls.api.organizations.clientRecords.clientInvoices(organizationId, clientRecordId),
            );
        },
    });
    useQueryErrorNotify(invoicesQuery, intl('failedToLoadInvoices'));

    const hasInvoiceId = !!invoiceId && invoiceId !== 'undefined' && invoiceId !== 'null';
    const invoiceQuery = useQuery({
        queryKey: getOrganizationClientRecordInvoiceCacheKey(clientRecordId, invoiceId!),
        queryFn: async () => {
            if (!hasInvoiceId) return null;
            return await appAPI.get<APITypes.organizationService.GetOrganizationClientRecordInvoiceResponse>(
                urls.api.organizations.clientRecords.clientInvoice(organizationId, clientRecordId, invoiceId!),
            );
        },
        enabled: hasInvoiceId,
    });
    useQueryErrorNotify(invoiceQuery, intl('failedToLoadInvoice'));

    const invoiceItemsQuery = useQuery({
        queryKey: getOrganizationClientRecordInvoiceInvoiceItemsCacheKey(clientRecordId, invoiceId!),
        queryFn: async () => {
            if (!hasInvoiceId) return null;
            return await appAPI.get<APITypes.organizationService.ListOrganizationClientRecordInvoiceItemsResponse>(
                urls.api.organizations.clientRecords.invoiceInvoiceItems(organizationId, clientRecordId, invoiceId!),
            );
        },
        enabled: hasInvoiceId,
    });
    useQueryErrorNotify(invoiceItemsQuery, intl('failedToLoadInvoiceItems'));

    const sendInvoiceMutation = useMutation({
        mutationFn: async (invoice: APITypes.organizationService.CreateOrganizationClientRecordInvoiceRequest) => {
            return await appAPI.post(
                urls.api.organizations.clientRecords.clientInvoices(organizationId, clientRecordId),
                {
                    body: invoice,
                },
            );
        },
        onSuccess: () => {
            notify('success', intl('successfullySentInvoice'));
            onInvoiceSent?.();
            onClientRecordInvoicesUpdate.resetCache({ organizationClientRecordId: clientRecordId })();
        },
        onError: () => {
            notify('error', intl('failedToSendInvoice'));
        },
    });

    const voidInvoiceMutation = useMutation({
        mutationFn: async (invoiceId: string) => {
            return await appAPI.post(
                urls.api.organizations.clientRecords.voidInvoice(organizationId, clientRecordId, invoiceId),
            );
        },
        onSuccess: () => {
            notify('success', intl('successfullyVoidedInvoice'));
            onInvoiceVoided?.();
            onClientRecordInvoicesUpdate.resetCache({ organizationClientRecordId: clientRecordId })();
        },
        onError: () => {
            notify('error', intl('failedToVoidInvoice'));
        },
    });

    const markPaidInvoiceMutation = useMutation({
        mutationFn: async (invoiceId: string) => {
            return await appAPI.post(
                urls.api.organizations.clientRecords.markInvoiceAsPaid(organizationId, clientRecordId, invoiceId),
            );
        },
        onSuccess: () => {
            notify('success', intl('successfullyMarkedPaidInvoice'));
            onInvoiceMarkedPaid?.();
            onClientRecordInvoicesUpdate.resetCache({ organizationClientRecordId: clientRecordId })();
        },
        onError: () => {
            notify('error', intl('failedToMarkPaidInvoice'));
        },
    });

    const resendInvoiceMutation = useMutation({
        mutationFn: async (invoiceId: string) => {
            return await appAPI.post(
                urls.api.organizations.clientRecords.resendInvoice(organizationId, clientRecordId, invoiceId),
            );
        },
        onSuccess: () => {
            notify('success', intl('successfullyResentInvoice'));
            onInvoiceResent?.();
            onClientRecordInvoicesUpdate.resetCache({ organizationClientRecordId: clientRecordId })();
        },
        onError: () => {
            notify('error', intl('failedToResendInvoice'));
        },
    });

    const refundInvoiceMutation = useMutation({
        mutationFn: async (invoiceId: string) => {
            const body: APITypes.organizationService.RefundOrganizationClientRecordInvoiceRequest = {
                reason: 'requested_by_customer',
            };
            return await appAPI.post(
                urls.api.organizations.clientRecords.refundClientInvoice(organizationId, clientRecordId, invoiceId),
                {
                    body,
                },
            );
        },
        onSuccess: () => {
            notify('success', intl('successfullyRefundedInvoice'));
            onInvoiceRefunded?.();
            onClientRecordInvoicesUpdate.resetCache({ organizationClientRecordId: clientRecordId })();
        },
        onError: () => {
            notify('error', intl('failedToRefundInvoice'));
        },
    });

    return {
        sendInvoice: sendInvoiceMutation.mutate,
        isSendingInvoice: sendInvoiceMutation.isPending,
        voidInvoice: voidInvoiceMutation.mutate,
        isVoidingInvoice: voidInvoiceMutation.isPending,
        markPaidInvoice: markPaidInvoiceMutation.mutate,
        isMarkingPaidInvoice: markPaidInvoiceMutation.isPending,
        resendInvoice: resendInvoiceMutation.mutate,
        isResendingInvoice: resendInvoiceMutation.isPending,
        invoices: invoicesQuery.data || null,
        isLoadingInvoices: invoicesQuery.isLoading,
        isLoadingInvoice: invoicesQuery.isLoading,
        invoice: invoiceQuery.data || null,
        isLoadingInvoiceItems: invoiceItemsQuery.isLoading,
        invoiceItems: invoiceItemsQuery.data || null,
        refundInvoice: refundInvoiceMutation.mutate,
        isRefundingInvoice: refundInvoiceMutation.isPending,
    };
}
